import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
import { campusApiClient } from '@datacamp/campus-integration';
import React from 'react';
import { hideCompletionPane, rateFeedback, showSCTs, takeHint, takeSolution } from './actions';
import { InstructionPane } from './InstructionPane';
import { useDispatch, useSelector } from './reduxHooks';
import { selectCompletionPaneVisible, selectFeedbackMessagesForExercise, selectHintShown, selectLostXp, selectSolutionShown, selectSuccessMessage } from './selectors';
import { jsx as ___EmotionJSX } from "@emotion/react";
export const ConnectedInstructionPane = _ref => {
  let {
    assignment,
    children,
    className,
    customAssignmentItem,
    customInstructionsHeader,
    disableStickyHeadings,
    enableResources,
    environment,
    exerciseId,
    exerciseSubtitle,
    footer,
    hideOpenAIBranding,
    hideTakeHint,
    hint,
    instructionSubtitle,
    isCloudExercise,
    isLoggedIn,
    isRatingPermitted,
    maxTimesToShowSolution,
    newOutputs,
    onClickCollapse,
    onContinue,
    onError,
    onTakeHint,
    onTakeSolution,
    onToggleCompletionPane,
    onUpdateXp,
    padBottom,
    resources,
    solutionInstructions,
    solutionMessage,
    title,
    xp
  } = _ref;
  const dispatch = useDispatch();
  const onCloseCompletionPane = React.useCallback(() => {
    dispatch(hideCompletionPane(exerciseId));
  }, [dispatch, exerciseId]);
  const successMessage = useSelector(state => selectSuccessMessage(state, exerciseId));
  const completionPaneVisible = useSelector(state => selectCompletionPaneVisible(state, exerciseId));
  const lostXp = useSelector(state => selectLostXp(state, exerciseId));
  const hintShown = useSelector(state => selectHintShown(state, exerciseId));
  const solutionShown = useSelector(state => selectSolutionShown(state, exerciseId));
  const feedbackMessages = useSelector(state => selectFeedbackMessagesForExercise(state, exerciseId));
  const hintCost = 0.3 * xp;
  const remainingXp = Math.max(xp - lostXp, 0);
  const onRate = React.useCallback(async payload => {
    try {
      await campusApiClient(environment).rateExercise(exerciseId, payload);
    } catch (e) {
      // @ts-expect-error fix later
      onError(e);
    }
  }, [environment, exerciseId, onError]);
  const onRateFeedback = React.useCallback(settings => {
    dispatch(rateFeedback(_objectSpread(_objectSpread({}, settings), {}, {
      environment,
      exerciseId,
      onError
    })));
  }, [dispatch, environment, exerciseId, onError]);
  const onTakeHintCb = React.useCallback(() => {
    dispatch(takeHint({
      environment,
      exerciseId,
      isLoggedIn,
      message: hint,
      onError,
      xpCost: hintCost
    }));
    onTakeHint === null || onTakeHint === void 0 || onTakeHint();
  }, [dispatch, environment, exerciseId, isLoggedIn, hint, onError, hintCost, onTakeHint]);
  const onTakeSolutionCb = React.useCallback(() => {
    dispatch(takeSolution({
      environment,
      exerciseId,
      isLoggedIn,
      message: solutionMessage,
      onError,
      solutionInstructions,
      xpCost: remainingXp
    }));
    onTakeSolution === null || onTakeSolution === void 0 || onTakeSolution();
  }, [dispatch, environment, exerciseId, solutionInstructions, isLoggedIn, solutionMessage, onError, remainingXp, onTakeSolution]);
  React.useEffect(() => {
    dispatch(hideCompletionPane(exerciseId));
  }, [dispatch, exerciseId]);
  React.useEffect(() => {
    dispatch(showSCTs({
      exerciseId,
      isLoggedIn,
      newOutputs
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newOutputs]); // add new SCTs only when the array has a new reference

  React.useEffect(() => onToggleCompletionPane === null || onToggleCompletionPane === void 0 ? void 0 : onToggleCompletionPane({
    completionPaneVisible
  }), [completionPaneVisible, onToggleCompletionPane]);
  React.useEffect(() => {
    onUpdateXp === null || onUpdateXp === void 0 || onUpdateXp({
      exerciseId,
      lostXp,
      remainingXp,
      xp
    });
  }, [exerciseId, onUpdateXp, remainingXp, xp, lostXp]);
  return ___EmotionJSX(InstructionPane, {
    assignment: assignment,
    className: className,
    completionPaneVisible: completionPaneVisible,
    customAssignmentItem: customAssignmentItem,
    customInstructionsHeader: customInstructionsHeader,
    disableStickyHeadings: disableStickyHeadings,
    enableResources: enableResources,
    exerciseId: exerciseId,
    exerciseSubtitle: exerciseSubtitle,
    feedbackMessages: feedbackMessages,
    footer: footer,
    hideOpenAIBranding: hideOpenAIBranding,
    hideTakeHint: hideTakeHint,
    hintCost: hintCost,
    hintShown: hintShown,
    instructionSubtitle: instructionSubtitle,
    isCloudExercise: isCloudExercise,
    isRatingPermitted: isRatingPermitted,
    maxTimesToShowSolution: maxTimesToShowSolution,
    onClickCollapse: onClickCollapse,
    onCloseCompletionPane: onCloseCompletionPane,
    onContinue: onContinue,
    onRate: onRate,
    onRateFeedback: onRateFeedback,
    onTakeHint: onTakeHintCb,
    onTakeSolution: onTakeSolution != null ? onTakeSolutionCb : undefined,
    padBottom: padBottom,
    remainingXp: remainingXp,
    resources: resources,
    solutionShown: solutionShown,
    successMessage: successMessage,
    title: title
  }, children);
};